import { useState, useEffect, useCallback, useId } from 'react';
import { supabase } from '../lib/supabase';

/** Disparado quando o parceiro altera o estabelecimento (ex.: nome). Reforça refetch nas telas turista/mapa. */
export const ESTABLISHMENT_DATA_CHANGED = 'facilita-establishment-data-changed';

export interface Category {
    id: string;
    slug: string;
    label: string;
    icon: string;
    color: string;
}

/**
 * Texto da seção "Sobre" no app. A coluna canônica (salva pelo portal) é `description`.
 * Só usamos `about` / `publicDescription` etc. quando `description` está vazio — evita texto demo
 * antigo ou de outro cadastro em `about` substituir a descrição real mais curta em `description`.
 */
export function pickPublicEstablishmentDescription(raw: unknown): string {
    const row = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
    if (!row) return '';
    const primary = row['description'];
    const primaryStr = primary == null ? '' : String(primary).trim();
    if (primaryStr) return primaryStr;

    const fallbackKeys = [
        'about',
        'aboutText',
        'about_text',
        'publicDescription',
        'public_description',
    ] as const;
    for (const key of fallbackKeys) {
        const v = row[key];
        if (v == null) continue;
        const text = String(v).trim();
        if (text) return text;
    }
    return '';
}

export interface Establishment {
    id: string;
    name: string;
    image: string;
    gallery?: string[];
    video?: string;
    distance: string;
    discount?: string;
    rating: number;
    address: string;
    description: string;
    phone?: string;
    openHours?: string;
    latitude?: number;
    longitude?: number;
    categoryId: string;
    category?: Category;
}

/** Estabelecimento com lat/lng já resolvidos (mapa / bottom sheet). */
export type EstablishmentMapPoint = Establishment & { lat: number; lng: number };

/** Unifica nomes de colunas vindos do PostgREST (`lat`/`lng` vs `latitude`/`longitude`). */
function normalizeEstablishmentRow(raw: unknown): Establishment {
    const row = raw as Establishment & { lat?: number; lng?: number };
    const latRaw = row.latitude ?? row.lat;
    const lngRaw = row.longitude ?? row.lng;
    const latitude =
        typeof latRaw === 'number' && Number.isFinite(latRaw)
            ? latRaw
            : latRaw != null
              ? Number(latRaw)
              : undefined;
    const longitude =
        typeof lngRaw === 'number' && Number.isFinite(lngRaw)
            ? lngRaw
            : lngRaw != null
              ? Number(lngRaw)
              : undefined;
    return {
        ...row,
        latitude: latitude != null && Number.isFinite(latitude) ? latitude : undefined,
        longitude: longitude != null && Number.isFinite(longitude) ? longitude : undefined,
        description: pickPublicEstablishmentDescription(row),
    };
}

/** Inscreve em `postgres_changes` na tabela Establishment; exige a tabela na publicação `supabase_realtime` (ver `sql/realtime_establishment.sql`). */
function subscribeToEstablishmentTable(
    channelName: string,
    filter: string | undefined,
    onChange: () => void,
) {
    const payload: {
        event: '*';
        schema: 'public';
        table: 'Establishment';
        filter?: string;
    } = { event: '*', schema: 'public', table: 'Establishment' };
    if (filter) payload.filter = filter;

    const channel = supabase
        .channel(channelName)
        .on('postgres_changes', payload, onChange)
        .subscribe((status, err) => {
            if (!import.meta.env.DEV) return;
            if (status === 'SUBSCRIBED') {
                console.debug(`[Realtime] ${channelName}: inscrito (Establishment)`);
            }
            if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
                console.warn(
                    `[Realtime] Establishment — ${status}. Ative a tabela na publicação supabase_realtime (sql/realtime_establishment.sql).`,
                    err,
                );
            }
        });
    return channel;
}

export interface Redemption {
    id: string;
    code: string;
    usedAt?: string;
    createdAt: string;
    userId: string;
    promotionId?: string;
    promotion?: Promotion;
    establishmentId: string;
    establishment: Establishment;
    purchaseAmount?: number;
    discountSaved?: number;
}

export interface Itinerary {
    id: string;
    title: string;
    subtitle: string;
    image: string;
    description?: string;
    duration?: string;
    cover_image?: string;
    gallery?: string[];
    video_url?: string;
    active?: boolean;
}

export interface User {
    id: string;
    name: string;
    email: string;
    phone?: string;
    avatar?: string;
    status: string;
    visitCount: number;
    checkIn?: string;
    checkOut?: string;
}

export interface Level {
    id: string;
    slug: string;
    minVisits: number;
    minDiscount?: number;
    maxDiscount?: number;
    title: string;
    discount: string;
    color: string;
    sortOrder: number;
}

export interface Notification {
    id: string;
    type: string;
    title: string;
    message: string;
    read: boolean;
    createdAt: string;
    userId: string;
}

export interface Promotion {
    id: string;
    title: string;
    discount: string;
    /** Se preenchido, o cupom não aparece na página do estabelecimento; só é resgatado digitando este código. */
    exclusiveCouponCode?: string | null;
    /** Percentual usado no cálculo da economia quando a promo é personalizada (texto livre em `discount`). */
    customDiscountPercent?: number | null;
    discountBronze?: number;
    discountSilver?: number;
    discountGold?: number;
    maxUses: number;
    currentUses: number;
    maxUsesPerUser?: number;
    startDate: string;
    endDate: string;
    active: boolean;
    establishmentId: string;
    establishment?: Establishment;
}

/** Cupom exclusivo (influenciador etc.): não listado na página pública. */
export function promotionIsExclusive(promo: Promotion): boolean {
    return Boolean(promo.exclusiveCouponCode && String(promo.exclusiveCouponCode).trim() !== '');
}

/** True se a promoção usa descontos diferentes por nível (Bronze/Prata/Ouro). */
export function promotionUsesLevelDiscounts(promo: Promotion): boolean {
    return (
        promo.discountBronze != null ||
        promo.discountSilver != null ||
        promo.discountGold != null
    );
}

/** Percentual para estimativa de economia em promoções sem níveis (nome livre + % guardado no banco). */
export function getCustomPromotionPercentForSavings(promo: Promotion): number | null {
    if (promotionUsesLevelDiscounts(promo)) return null;
    const n = promo.customDiscountPercent;
    if (n != null && Number.isFinite(Number(n)) && Number(n) > 0) return Number(n);
    const d = promo.discount || '';
    if (d.includes('%')) {
        const p = parseFloat(d.replace(/[^0-9.]/g, ''));
        return !Number.isNaN(p) ? p : null;
    }
    return null;
}

/** Resumo do desconto para listas no portal do lojista. */
export function getPromotionDiscountSummary(promo: Promotion): string {
    if (promotionUsesLevelDiscounts(promo)) {
        return `Bronze ${promo.discountBronze ?? '-'}% | Prata ${promo.discountSilver ?? '-'}% | Ouro ${promo.discountGold ?? '-'}%`;
    }
    const label = promo.discount?.trim() || 'Desconto personalizado';
    const pct = getCustomPromotionPercentForSavings(promo);
    const suffix = promotionIsExclusive(promo) ? ' · oculto (só com código)' : '';
    if (pct != null) return `${label} · ${pct}%${suffix}`;
    return `${label}${suffix}`;
}

/** Retorna o desconto em texto para o nível do usuário (ex: "5% OFF") ou o texto único da promoção personalizada. */
export function getPromoDiscountForLevel(promo: Promotion, levelSlug: string): string {
    if (!promotionUsesLevelDiscounts(promo)) {
        return promo.discount?.trim() || 'Oferta especial';
    }
    const pct =
        levelSlug === 'gold' ? promo.discountGold :
        levelSlug === 'silver' ? promo.discountSilver :
        promo.discountBronze;
    if (pct != null) return `${pct}% OFF`;
    return promo.discount || 'Oferta especial';
}

export interface Merchant {
    id: string;
    email: string;
    establishmentId: string;
    establishment?: Establishment;
}

export function useCategories() {
    const [data, setData] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        supabase.from('Category').select('*').order('label')
            .then(({ data: rows, error: err }) => {
                if (cancelled) return;
                if (err) { setError('Erro ao carregar categorias'); }
                else { setData(rows || []); }
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);
    return { data, loading, error };
}

export function useLevels() {
    const [data, setData] = useState<Level[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        supabase.from('Level').select('*').order('sortOrder', { ascending: true })
            .then(({ data: rows, error: err }) => {
                if (cancelled) return;
                if (err) { setError('Erro ao carregar níveis'); }
                else { setData(rows || []); }
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);
    return { data, loading, error };
}

export function useEstablishments(categoryId?: string) {
    const [data, setData] = useState<Establishment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const hookInstanceId = useId().replace(/[^a-zA-Z0-9]/g, '');

    useEffect(() => {
        let cancelled = false;

        const load = (isInitial: boolean) => {
            let query = supabase.from('Establishment').select('*, category:Category(*)');
            if (categoryId) query = query.eq('categoryId', categoryId);

            query.then(({ data: rows, error: err }) => {
                if (cancelled) return;
                if (err) {
                    console.error('[useEstablishments] Erro ao carregar lista:', err);
                    if (isInitial) setError('Erro ao carregar estabelecimentos');
                } else {
                    setData((rows || []).map(normalizeEstablishmentRow));
                    setError(null);
                }
                if (isInitial) setLoading(false);
            });
        };

        const refetch = () => {
            if (!cancelled) load(false);
        };

        setLoading(true);
        load(true);

        const channelName = `establishments-list-${categoryId ?? 'all'}-${hookInstanceId}`;
        const channel = subscribeToEstablishmentTable(channelName, undefined, refetch);

        const onVisibility = () => {
            if (document.visibilityState === 'visible') refetch();
        };
        const onPageShow = (e: Event) => {
            if ((e as PageTransitionEvent).persisted) refetch();
        };

        const onEstablishmentDataChanged = () => {
            if (!cancelled) refetch();
        };
        window.addEventListener(ESTABLISHMENT_DATA_CHANGED, onEstablishmentDataChanged);

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', refetch);
        window.addEventListener('pageshow', onPageShow);

        return () => {
            cancelled = true;
            window.removeEventListener(ESTABLISHMENT_DATA_CHANGED, onEstablishmentDataChanged);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', refetch);
            window.removeEventListener('pageshow', onPageShow);
            void supabase.removeChannel(channel);
        };
    }, [categoryId, hookInstanceId]);
    return { data, loading, error };
}

export function useEstablishment(id: string) {
    const [data, setData] = useState<Establishment | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const hookInstanceId = useId().replace(/[^a-zA-Z0-9]/g, '');

    useEffect(() => {
        if (!id) return;
        let cancelled = false;

        const load = (isInitial: boolean) => {
            supabase.from('Establishment').select('*, category:Category(*)').eq('id', id).single()
                .then(({ data: row, error: err }) => {
                    if (cancelled) return;
                    if (err) {
                        console.error('[useEstablishment] Erro ao carregar:', err);
                        if (isInitial) setError('Erro ao carregar estabelecimento');
                    } else {
                        setData(row ? normalizeEstablishmentRow(row) : null);
                        setError(null);
                    }
                    if (isInitial) setLoading(false);
                });
        };

        const refetch = () => {
            if (!cancelled) load(false);
        };

        setLoading(true);
        load(true);

        const channelName = `establishment-row-${id}-${hookInstanceId}`;
        const filter = `id=eq.${id}`;
        const channel = subscribeToEstablishmentTable(channelName, filter, refetch);

        const onVisibility = () => {
            if (document.visibilityState === 'visible') refetch();
        };
        const onPageShow = (e: Event) => {
            if ((e as PageTransitionEvent).persisted) refetch();
        };

        const onEstablishmentDataChanged = (ev: Event) => {
            const eid = (ev as CustomEvent<{ id?: string }>).detail?.id;
            if (!cancelled && eid != null && String(eid) === String(id)) refetch();
        };
        window.addEventListener(ESTABLISHMENT_DATA_CHANGED, onEstablishmentDataChanged);

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', refetch);
        window.addEventListener('pageshow', onPageShow);

        return () => {
            cancelled = true;
            window.removeEventListener(ESTABLISHMENT_DATA_CHANGED, onEstablishmentDataChanged);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', refetch);
            window.removeEventListener('pageshow', onPageShow);
            void supabase.removeChannel(channel);
        };
    }, [id, hookInstanceId]);
    return { data, loading, error };
}

export function useItineraries() {
    const [data, setData] = useState<Itinerary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        supabase.from('Itinerary').select('*').eq('active', true)
            .then(({ data: rows, error: err }) => {
                if (cancelled) return;
                if (err) { setError('Erro ao carregar roteiros'); }
                else { setData(rows || []); }
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);
    return { data, loading, error };
}

export function useItinerary(id: string) {
    const [data, setData] = useState<Itinerary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        supabase.from('Itinerary').select('*').eq('id', id).single()
            .then(({ data: row, error: err }) => {
                if (cancelled) return;
                if (err) { setError('Roteiro não encontrado'); }
                else { setData(row); }
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [id]);
    return { data, loading, error };
}

export function useUsers() {
    const [data, setData] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        supabase.from('User').select('*').order('createdAt', { ascending: false })
            .then(({ data: rows, error: err }) => {
                if (cancelled) return;
                if (err) { setError('Erro ao carregar usuários'); }
                else { setData(rows || []); }
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);
    return { data, loading, error };
}

export function useUser(id: string) {
    const [data, setData] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        supabase.from('User').select('*').eq('id', id).single()
            .then(({ data: row, error: err }) => {
                if (cancelled) return;
                if (err) { setError('Erro ao carregar usuário'); }
                else { setData(row); }
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [id]);
    return { data, loading, error };
}

export function useNotifications(userId?: string) {
    const [data, setData] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchNotifications = useCallback(() => {
        if (!userId) {
            setData([]);
            setLoading(false);
            return;
        }
        supabase.from('Notification').select('*')
            .eq('userId', userId)
            .order('createdAt', { ascending: false })
            .then(({ data: rows, error: err }) => {
                if (err) { setError('Erro ao carregar notificações'); setData([]); }
                else { setData(rows || []); }
                setLoading(false);
            });
    }, [userId]);

    useEffect(() => {
        let cancelled = false;
        queueMicrotask(() => {
            if (!cancelled) fetchNotifications();
        });
        return () => {
            cancelled = true;
        };
    }, [fetchNotifications]);

    const markAsRead = async (id: string) => {
        await supabase.from('Notification').update({ read: true }).eq('id', id);
        fetchNotifications();
    };

    const markAllAsRead = async (uid: string) => {
        await supabase.from('Notification').update({ read: true }).eq('userId', uid);
        fetchNotifications();
    };

    const deleteNotification = async (id: string) => {
        await supabase.from('Notification').delete().eq('id', id);
        fetchNotifications();
    };

    const createNotification = async (title: string, message: string, type: string = 'system') => {
        if (!userId) return;
        await supabase.from('Notification').insert({ userId, title, message, type });
        fetchNotifications();
    };

    return { data, loading, error, markAsRead, markAllAsRead, deleteNotification, createNotification, refetch: fetchNotifications };
}

export type UsePromotionsOptions = {
    /** Se true, não retorna cupons com código exclusivo (para página pública do estabelecimento). */
    publicOnly?: boolean;
};

export function usePromotions(establishmentId?: string, options?: UsePromotionsOptions, refreshKey = 0) {
    const [data, setData] = useState<Promotion[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const publicOnly = options?.publicOnly ?? false;

    useEffect(() => {
        let cancelled = false;
        let query = supabase.from('Promotion').select('*, establishment:Establishment(id, name)');
        if (establishmentId) query = query.eq('establishmentId', establishmentId);
        if (publicOnly) query = query.is('exclusiveCouponCode', null);

        query.then(({ data: rows, error: err }) => {
            if (cancelled) return;
            if (err) { setError('Erro ao carregar promoções'); }
            else { setData(rows || []); }
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [establishmentId, publicOnly, refreshKey]);
    return { data, loading, error };
}

export interface MerchantStats {
    views: number;
    couponsUsed: number;
    newCustomers: number;
    estimatedRevenue: number;
    totalPurchaseAmount?: number;
    totalDiscountSaved?: number;
    commissionRate?: number;
    platformFee?: number;
}

export interface MerchantRecentSale {
    id: string;
    code: string;
    usedAt: string;
    purchaseAmount?: number;
    discountSaved?: number;
    userName: string;
    promotionTitle?: string;
    promotionDiscount?: string;
}

export function useMerchantStats(establishmentId: string) {
    const [data, setData] = useState<MerchantStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!establishmentId) return;
        let cancelled = false;
        supabase.rpc('get_merchant_stats', { p_establishment_id: establishmentId })
            .then(({ data: result, error: err }) => {
                if (cancelled) return;
                if (err) { setError('Erro ao carregar estatísticas'); }
                else { setData(result); }
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [establishmentId]);
    return { data: data as MerchantStats | null, loading, error };
}

export function useMerchantRecentSales(establishmentId: string, limit = 15) {
    const [data, setData] = useState<MerchantRecentSale[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!establishmentId) return;
        let cancelled = false;
        supabase.rpc('get_merchant_recent_sales', { p_establishment_id: establishmentId, p_limit: limit })
            .then(({ data: result, error: err }) => {
                if (cancelled) return;
                if (err) { setError('Erro ao carregar vendas'); setData([]); }
                else { setData(Array.isArray(result) ? result : []); }
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [establishmentId, limit]);
    return { data, loading, error };
}

export interface PlatformFeeSummary {
    totalFee: number;
    paid: number;
    pending: number;
    commissionRate: number | null;
}

export interface PlatformPaymentRow {
    id: string;
    amount: number;
    currency: string;
    status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'in_process';
    paymentMethod: 'checkout_pro' | 'pix';
    mpPaymentId: string | null;
    mpOrderId: string | null;
    mpOrderStatus: string | null;
    statusDetail: string | null;
    preferenceId: string | null;
    expiresAt: string | null;
    createdAt: string;
    approvedAt: string | null;
}

/**
 * Cobrança da taxa da plataforma (Mercado Pago).
 * - `summary` lê de `get_platform_fee_summary`.
 * - `payments` é o histórico via `list_platform_payments`.
 * - `refresh` força refetch após criar pagamento ou voltar do checkout.
 */
export function useMerchantPlatformPayments(establishmentId: string) {
    const [summary, setSummary] = useState<PlatformFeeSummary | null>(null);
    const [payments, setPayments] = useState<PlatformPaymentRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

    useEffect(() => {
        if (!establishmentId) return;
        let cancelled = false;
        Promise.all([
            supabase.rpc('get_platform_fee_summary', { p_establishment_id: establishmentId }),
            supabase.rpc('list_platform_payments', { p_establishment_id: establishmentId, p_limit: 30 }),
        ]).then(([summaryRes, listRes]) => {
            if (cancelled) return;
            if (summaryRes.error) {
                setError('Erro ao carregar taxa pendente');
                console.warn('[useMerchantPlatformPayments] summary', summaryRes.error);
            } else {
                const s = summaryRes.data as Record<string, unknown> | null;
                if (s) {
                    setSummary({
                        totalFee: Number(s.totalFee) || 0,
                        paid: Number(s.paid) || 0,
                        pending: Number(s.pending) || 0,
                        commissionRate: s.commissionRate != null ? Number(s.commissionRate) : null,
                    });
                }
            }
            if (!listRes.error && Array.isArray(listRes.data)) {
                setPayments(listRes.data as PlatformPaymentRow[]);
            }
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [establishmentId, refreshKey]);

    return { summary, payments, loading, error, refresh };
}

export function useRedemptions(userId?: string) {
    const [data, setData] = useState<Redemption[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        supabase.from('Redemption')
            .select('*, promotion:Promotion(*), establishment:Establishment(*)')
            .eq('userId', userId)
            .order('createdAt', { ascending: false })
            .then(({ data: rows, error: err }) => {
                if (cancelled) return;
                if (err) { setError('Erro ao carregar resgates'); }
                else { setData(rows || []); }
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [userId]);
    return { data, loading, error };
}
